import React, { useState, useCallback, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import BarcodeScanner from "../barcode-scanner";
import { CameraFill } from "react-bootstrap-icons";
import { useCircleDarkMode } from "../../hooks/useCircleDarkMode";
import { useDebugMode } from "../../hooks/useDebugMode";
import { LocationPosition, Warehouse } from "../../workers/location";
import { ButtonVariant } from "react-bootstrap/esm/types";
import { useLocalStorage } from "usehooks-ts";
import { LoadRefData } from "../../external/aws-data-reporting/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { SearchIconWithPopup } from "../search-icon-option-modal";

const createLocationWorker = createWorkerFactory(
  () => import("../../workers/location")
);

const createLoadReferenceWorker = createWorkerFactory(
  () => import("../../workers/load-references")
);

interface BarcodeResult {
  getText: () => string;
}

const sanitizeInput = (input: string): string => {
  return input.startsWith("URL:") ? input.slice(4) : input;
};

export const ItemLocationScanner = () => {
  const [showBarcodeScanner, setShowBarcodeScanner] = useState<boolean>(false);
  const [itemValue, setItemValue] = useState<string>("");
  const [locationValue, setLocationValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string>();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isScanned, setIsScanned] = useState<boolean>(false); // Flag to check if input is scanned
  const [itemConfirmed, setItemConfirmed] = useState<boolean>(false); // Flag to check if item input is confirmed
  const [confirmedItem, setConfirmedItem] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [locationRequiresInternalOffset, setLocationRequiresInternalOffset] =
    useState<boolean>(false);
  const [selectedRackLocation, setSelectedRackLocation] = useState<
    LocationPosition | undefined
  >();
  const [warehouse, setWarehouse] = useLocalStorage<Warehouse>(
    "warehouse",
    "Retail"
  );
  const [loadReferences, setLoadReferences] = useState<LoadRefData[]>([]);

  const [isLoadingReferences, setIsLoadingReferences] = useState(false);
  const [loadReferencesError, setLoadReferencesError] = useState("");
  const [selectedLoadRef, setSelectedLoadRef] = useState<LoadRefData>();

  const darkMode = useCircleDarkMode();
  const debugMode = useDebugMode();

  const scannerInboundStorageScan = useFeatureFlagEnabled(
    "scanner_inboundStorage"
  );

  const loadReferenceWorker = useWorker(createLoadReferenceWorker);

  if (debugMode) {
    document.addEventListener("keydown", function (e) {
      console.table({
        keyCodeDEP: e.which,
        key: e.key,
        code: e.code,
        location: e.location,
      });
    });
  }

  const locationWorker = createLocationWorker();
  const itemInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  const onCameraBarcodeDetected = useCallback(
    (barcode: BarcodeResult) => {
      setShowBarcodeScanner(false);
      const v = sanitizeInput(barcode.getText());
      if (itemConfirmed) {
        setLocationValue(v.toUpperCase());
        setIsScanned(true);
        // Submit the form after setting the location value if scanned
        if (locationValue) {
          document
            .getElementById("location-form")
            ?.dispatchEvent(
              new Event("submit", { cancelable: true, bubbles: true })
            );
        }
      } else {
        // It is the item scan
        setItemValue(v.toUpperCase());
        setItemConfirmed(true);
        setConfirmedItem(v.toUpperCase());
        locationInputRef.current?.focus();
      }
    },
    [itemConfirmed, locationValue]
  );

  const storeValues = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    setSubmitError(undefined);
    if (debugMode) {
      console.debug("Sending item and location values to worker...");
    }

    if (!confirmedItem || !locationValue) {
      setShowErrorModal(true);
      setSuccess(undefined);
      return;
    }

    setIsLoading(true);
    setSuccess(undefined);

    try {
      const rackLocationForStorage =
        selectedRackLocation === "NON-RACK" ? undefined : selectedRackLocation;

      if (warehouse === "InboundStorage" && !selectedLoadRef) {
        throw new Error("selectedLoadRef not set");
      }

      // If we're in InboundStorage and have a selectedLoadRef, add the additional parameters
      if (warehouse === "InboundStorage" && selectedLoadRef) {
        // Add loadRef and deliveryDate to the parameters
        await locationWorker.storeItemsLocation(
          [itemValue],
          locationValue,
          warehouse,
          rackLocationForStorage,
          selectedLoadRef.loadRef,
          selectedLoadRef.deliverydate
        );
      } else {
        // Call with just the base parameters
        await locationWorker.storeItemsLocation(
          [itemValue],
          locationValue,
          warehouse,
          rackLocationForStorage
        );
      }

      // Reset form to default values
      setItemValue("");
      setLocationValue("");
      setItemConfirmed(false);
      setSelectedRackLocation(undefined);
      setSuccess(
        `Item ${itemValue} stored in location ${locationValue}${
          rackLocationForStorage ? ` (section ${rackLocationForStorage})` : ""
        } in ${warehouse} warehouse.`
      );
      itemInputRef.current?.focus();
      // Clear the success message after a wait
      setTimeout(() => {
        setSuccess(undefined);
      }, 4000);
    } catch (err) {
      let errorMessage = `Error occurred saving item ${itemValue}. Please try to confirm again.`;
      setSuccess(undefined);
      if (err instanceof Error && err.message === "selectedLoadRef not set") {
        errorMessage = "Please select the load reference";
      }
      setSubmitError(errorMessage);
      setTimeout(() => {
        setSubmitError(undefined);
      }, 5000);
      console.error("Error occurred storing item:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWarehouseChange = (warehouseValue: string) => {
    if (warehouseValue === "InboundStorage" && !scannerInboundStorageScan) {
      setSubmitError("Inbound Storage scanning is not currently available");
      return;
    }

    setWarehouse(warehouseValue as Warehouse);
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(undefined);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined; // to avoid es-lint warning, explicitly return undefined
  }, [success]);

  useEffect(() => {
    if (itemConfirmed && locationValue) {
      // TODO: Filter if this is required based on location value
      setLocationRequiresInternalOffset(true);
    } else {
      setLocationRequiresInternalOffset(false);
    }
  }, [itemConfirmed, locationValue]);

  useEffect(() => {
    if (warehouse === "InboundStorage") {
      const fetchLoadReferences = async () => {
        setIsLoadingReferences(true);
        setLoadReferencesError("");
        try {
          const fetchedLoadReferences =
            await loadReferenceWorker.getLoadReferences();
          if (!fetchedLoadReferences) throw new Error("No load references");
          setLoadReferences(fetchedLoadReferences);
        } catch (e) {
          console.error("Error fetching load references:", e);
          setLoadReferencesError(
            "Failed to load references. Please try again."
          );
          setLoadReferences([]);
        } finally {
          setIsLoadingReferences(false);
        }
      };
      fetchLoadReferences();
    } else {
      setSelectedLoadRef(undefined);
    }
  }, [warehouse, loadReferenceWorker]);

  const iconWidthAndHeight = 48;

  const iconStyles = {
    width: iconWidthAndHeight,
    height: iconWidthAndHeight,
    style: { cursor: "pointer" },
  };

  useEffect(() => {
    // TODO: leave this in for now for debugging purposes on prod
    // if (debugMode) {
    console.table({
      debugMode,
      itemValue,
      locationValue,
      isScanned,
      itemConfirmed,
      confirmedItem,
      submitError,
      locationRequiresInternalOffset,
      selectedRackLocation,
    });
    // }
  }, [
    confirmedItem,
    debugMode,
    isScanned,
    itemConfirmed,
    itemValue,
    locationRequiresInternalOffset,
    locationValue,
    selectedRackLocation,
    submitError,
  ]);

  const locationButtonVariantNotSelected: ButtonVariant = "outline-dark";
  const locationButtonVariantSelected: ButtonVariant = "success";

  const LocationButton = ({
    location,
    description,
    type,
    selectedLocation,
    setSelectedLocation,
  }: {
    location: LocationPosition | Warehouse;
    description: string;
    type: "rack" | "warehouse"; // Can be extended for more types
    selectedLocation: LocationPosition | Warehouse | undefined;
    setSelectedLocation: (
      location: LocationPosition | Warehouse | undefined
    ) => void;
  }) => (
    <Button
      variant={
        selectedLocation === location
          ? locationButtonVariantSelected
          : locationButtonVariantNotSelected
      }
      size="lg"
      onClick={() => setSelectedLocation(location)}
    >
      {description}
    </Button>
  );

  return (
    <Container>
      <Row>
        <Col>
          <h3>Location Scanner</h3>
        </Col>
        <Col className="d-flex justify-content-center">
          <CameraFill
            {...iconStyles}
            color={showBarcodeScanner ? "blue" : darkMode ? "white" : "black"}
            onClick={() => setShowBarcodeScanner(!showBarcodeScanner)}
          />
        </Col>
        <Col className="d-flex justify-content-center">
          <SearchIconWithPopup {...iconStyles} style={{ cursor: "pointer" }} />
        </Col>
      </Row>
      <Form
        id="location-form"
        onSubmit={(e) => {
          // If item is confirmed but no location is provided, then store the item as confirmed item
          if (itemValue && !locationValue) {
            e.preventDefault();
            e.stopPropagation();
            setItemConfirmed(true);
            setConfirmedItem(itemValue.toUpperCase());
            return false;
          } else if (itemValue && locationValue && !selectedRackLocation) {
            e.preventDefault();
            e.stopPropagation();
            setSubmitError("Please select a rack location.");
            setTimeout(() => {
              setSubmitError(undefined);
            }, 3000);
            return false;
          }
          storeValues(e);
          return true;
        }}
      >
        {showBarcodeScanner ? (
          <Row>
            <Col>
              <BarcodeScanner onBarcodeDetected={onCameraBarcodeDetected} />
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col>
            <b>Warehouse</b>
            <Form.Select
              size="lg"
              value={warehouse}
              onChange={(e) => handleWarehouseChange(e.target.value)}
              style={{ fontSize: "1.2em", height: "60px" }}
            >
              <option value="Retail">Retail</option>
              <option value="Auction">Auction</option>
              {scannerInboundStorageScan && (
                <option value="InboundStorage">Inbound Storage</option>
              )}
              <option value="Disposal">Disposal</option>
              <option value="SwyftParts">Swyft Parts</option>
            </Form.Select>
          </Col>
        </Row>
        <Row>
          {warehouse && warehouse === "InboundStorage" && loadReferences ? (
            <Form className="mt-2">
              {loadReferencesError ? (
                <Alert variant="danger" className="mt-2">
                  Error loading Load References
                </Alert>
              ) : (
                <Form.Select
                  onChange={(e) => {
                    const selectedIndex = parseInt(e.target.value, 10);
                    if (
                      !isNaN(selectedIndex) &&
                      selectedIndex >= 0 &&
                      selectedIndex < loadReferences.length
                    ) {
                      const fullRefObject = loadReferences[selectedIndex];
                      setSelectedLoadRef(fullRefObject);
                    } else {
                      setSelectedLoadRef(undefined);
                    }
                  }}
                  value={
                    selectedLoadRef
                      ? loadReferences
                          .findIndex(
                            (ref) =>
                              ref.loadRef === selectedLoadRef.loadRef &&
                              ref.deliverydate === selectedLoadRef.deliverydate
                          )
                          .toString()
                      : ""
                  }
                  disabled={isLoadingReferences}
                >
                  {isLoadingReferences ? (
                    <option value="">Loading...</option>
                  ) : (
                    <>
                      <option value="">Choose a load reference...</option>
                      {loadReferences.map((ref, index) => (
                        <option
                          key={`${ref.loadRef}-${ref.deliverydate}-${index}`}
                          value={index.toString()}
                        >
                          {ref.loadRef} - {ref.deliverydate} -{" "}
                          {ref.clientcode || "Unknown Supplier"}
                        </option>
                      ))}
                    </>
                  )}
                </Form.Select>
              )}
            </Form>
          ) : undefined}
        </Row>
        <Row>
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <b>Item</b>
            <Form.Control
              style={{
                fontSize: "1.5em",
                fontFamily: "Menlo, monospace, sans-serif",
                textAlign: "center",
              }}
              autoFocus
              type="text"
              name="type"
              ref={itemInputRef}
              placeholder="PMID or Pallet"
              value={sanitizeInput(itemValue).toUpperCase()}
              onChange={(e) => {
                setItemValue(sanitizeInput(e.target.value).toUpperCase());
                setIsScanned(false);
                setItemConfirmed(false);
              }}
            />
            {!itemConfirmed || confirmedItem !== itemValue ? (
              <Button
                variant="primary"
                size="lg"
                className="mt-2"
                onClick={() => {
                  setItemConfirmed(true);
                  setConfirmedItem(itemValue.toUpperCase());
                }}
                disabled={!itemValue}
                style={{
                  fontFamily: "Menlo, monospace, sans-serif",
                  textAlign: "center",
                  marginBottom: "0.5em",
                }}
              >
                Confirm Item
              </Button>
            ) : null}
          </Col>
        </Row>
        {itemConfirmed ? (
          <Row>
            <Col
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <b>Location</b>
              <Form.Control
                style={{
                  fontSize: "1.5em",
                  fontFamily: "Menlo, monospace, sans-serif",
                  textAlign: "center",
                }}
                autoFocus
                type="text"
                name="type"
                ref={locationInputRef}
                placeholder="Pallet or Warehouse Location"
                value={sanitizeInput(locationValue).toUpperCase()}
                onChange={(e) => {
                  setLocationValue(sanitizeInput(e.target.value).toUpperCase());
                  setIsScanned(false);
                }}
              />
            </Col>
          </Row>
        ) : null}
        {submitError && (
          <Row>
            <Col>
              <Alert variant="danger" className="mt-3">
                {submitError}
              </Alert>
            </Col>
          </Row>
        )}
        {locationRequiresInternalOffset ? (
          <div className="mt-3">
            <h4>Rack Location Selection</h4>
            <p>Please select the space taken up by the pallet/item.</p>
            <div>
              <Row className="mb-1">
                <Col className="d-grid">
                  <LocationButton
                    location="ABC"
                    description="Full Width"
                    type="rack"
                    selectedLocation={selectedRackLocation}
                    setSelectedLocation={(location) =>
                      setSelectedRackLocation(
                        location as LocationPosition | undefined
                      )
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="d-grid">
                  <ButtonGroup>
                    <LocationButton
                      location="AB"
                      description="Left Half"
                      type="rack"
                      selectedLocation={selectedRackLocation}
                      setSelectedLocation={(location) =>
                        setSelectedRackLocation(
                          location as LocationPosition | undefined
                        )
                      }
                    />
                    <LocationButton
                      location="BC"
                      description="Right Half"
                      type="rack"
                      selectedLocation={selectedRackLocation}
                      setSelectedLocation={(location) =>
                        setSelectedRackLocation(
                          location as LocationPosition | undefined
                        )
                      }
                    />
                  </ButtonGroup>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="d-grid">
                  <ButtonGroup>
                    <LocationButton
                      location="A"
                      description="Left Third"
                      type="rack"
                      selectedLocation={selectedRackLocation}
                      setSelectedLocation={(location) =>
                        setSelectedRackLocation(
                          location as LocationPosition | undefined
                        )
                      }
                    />
                    <LocationButton
                      location="B"
                      description="Middle Third"
                      type="rack"
                      selectedLocation={selectedRackLocation}
                      setSelectedLocation={(location) =>
                        setSelectedRackLocation(
                          location as LocationPosition | undefined
                        )
                      }
                    />
                    <LocationButton
                      location="C"
                      description="Right Third"
                      type="rack"
                      selectedLocation={selectedRackLocation}
                      setSelectedLocation={(location) =>
                        setSelectedRackLocation(
                          location as LocationPosition | undefined
                        )
                      }
                    />
                  </ButtonGroup>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="d-grid">
                  <LocationButton
                    location="NON-RACK"
                    description="Not a Rack Location"
                    type="rack"
                    selectedLocation={selectedRackLocation}
                    setSelectedLocation={(location) =>
                      setSelectedRackLocation(
                        location as LocationPosition | undefined
                      )
                    }
                  />
                </Col>
              </Row>
            </div>
          </div>
        ) : null}
        {itemValue && locationValue ? (
          <>
            <Row>
              <Col>
                Store item <br />
                <b>{itemValue}</b>
                <br /> in location <br />
                <b>{locationValue}</b>?
              </Col>
              <Col
                className="d-flex justify-content-end"
                style={{
                  fontSize: "1.5em",
                }}
              >
                <Button
                  variant="success"
                  type="submit"
                  size="lg"
                  disabled={
                    isLoading ||
                    !itemValue ||
                    !locationValue ||
                    !selectedRackLocation
                  }
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Storing...
                    </>
                  ) : (
                    "Confirm Location"
                  )}
                </Button>
              </Col>
            </Row>
          </>
        ) : null}
      </Form>
      {success && (
        <Row>
          <Col>
            <Alert variant="success" className="mt-3">
              {success}
            </Alert>
          </Col>
        </Row>
      )}
      {submitError && (
        <Row>
          <Col>
            <Alert variant="danger" className="mt-3">
              {submitError}
            </Alert>
          </Col>
        </Row>
      )}
      {showErrorModal && (
        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>Item value and location value are required.</Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowErrorModal(false)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default ItemLocationScanner;
